import type { FC, ReactNode } from 'react'

import { Plus } from 'lucide-react'
import ExpandView from '@/components/generals/expand-view'
import ExportFile from '@/components/landing/mockups/export-file'
import SectionIntro from '@/components/landing/section-intro'

const ExportSection: FC = (): ReactNode => {
    return (
        <section id='export' aria-labelledby='export-title' className='border-t border-border bg-tint py-20 lg:py-28'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20'>
                <figure className='reveal order-last lg:order-first'>
                    <div className='relative px-6 sm:px-10'>
                        <div role='img' aria-label='Illustration of an MP4 file holding a four-clip composition, with a download button'>
                            <ExportFile />
                        </div>

                        <ExpandView
                            title='Your composition as one MP4 file'
                            caption='Save to your device. Share wherever you like.'
                            ratio={0.82}
                            className='absolute bottom-10 right-2 sm:right-6'
                        >
                            <ExportFile className='max-w-none' />
                        </ExpandView>
                    </div>

                    <figcaption className='mt-6 text-center font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground'>
                        Save to your device. Share wherever you like.
                    </figcaption>
                </figure>

                <div>
                    <SectionIntro
                        id='export-title'
                        eyebrow='MP4 export'
                        title={(
                            <>
                                Your composition.
                                <br />
                                Ready to share.
                            </>
                        )}
                        intro='Export a scene of local videos and photos into a single MP4 file. No uploading your files to a cloud editor.'
                    />

                    <dl className='mt-9 flex flex-wrap gap-x-14 gap-y-6'>
                        <div>
                            <dt className='sr-only'>
                                Free export quality
                            </dt>

                            <dd className='font-heading text-[2rem] font-extrabold leading-none tracking-[-0.03em] text-brand'>
                                720p
                            </dd>

                            <dd className='mt-2 text-sm text-muted-foreground'>
                                Free · up to 30 seconds
                            </dd>
                        </div>

                        <div>
                            <dt className='sr-only'>
                                PRO export quality
                            </dt>

                            <dd className='font-heading text-[2rem] font-extrabold leading-none tracking-[-0.03em] text-hd'>
                                1080p
                            </dd>

                            <dd className='mt-2 text-sm text-muted-foreground'>
                                Available with PRO
                            </dd>
                        </div>
                    </dl>

                    <details className='disclosure group mt-9 border-y border-border'>
                        <summary className='flex cursor-pointer items-center justify-between gap-4 py-4 text-[0.95rem] font-medium text-foreground'>
                            What the export includes

                            <Plus className='size-4 shrink-0 text-muted-foreground transition-transform duration-300 group-open:rotate-45' />
                        </summary>

                        <p className='max-w-xl pb-5 text-sm leading-relaxed text-muted-foreground'>
                            Only the compatible local sources you choose. Playlists stay on their current item, animated frames are exported as still frames, and YouTube or network streams are left out.
                        </p>
                    </details>
                </div>
            </div>
        </section>
    )
}

export default ExportSection
